import queryString from 'query-string'
import * as typePP from 'shared/typePowerPlant'
import {Validator} from 'jsonschema'
import {Schemas, showErrorMessageInConsole} from 'modules/schema'
import {SortOrderEnum} from 'constants/sortOrder'
import get from 'lodash/get'
import {isApiEndpointEnabled, FlagPages} from 'config/featureFlags'
import axiosApiInstance from './axiosSettings'

const v = new Validator()

let API_URL = 'https://wt-res-dev.wt-dc5-dev.dtek.cloud/api'

const isLocalEnv = Boolean(process.env.REACT_APP_API_URL)

if (isLocalEnv) {
  API_URL = process.env.REACT_APP_API_URL
}

const API = {
  healthCheck: () =>
    axiosApiInstance.get(
      // TODO: check url
      'https://anl001-api-app-int.azurewebsites.net/HealthCheck/AuthTest',
    ),

  // Users API - controlled by feature flag
  ...(isApiEndpointEnabled(FlagPages.USERS_PAGE) && {
    getUsers: () => axiosApiInstance.get(`${API_URL}/users`),
  }),

  // Auth API
  getAuth: () => axiosApiInstance.get(`${API_URL}/Auth`),

  // Dashboard API methods
  getDataAllCharts: (typeChart, id, dateRange = {}) => {
    const partUrlParams = `from=${encodeURIComponent(dateRange.from)}&to=${encodeURIComponent(
      dateRange.to,
    )}`

    const requestUrl = id
      ? `${API_URL}/${typeChart}?id=${id}&${partUrlParams}`
      : `${API_URL}/${typeChart}?${partUrlParams}`

    return axiosApiInstance.get(requestUrl)
  },

  getChartsLiveData: (typeChart, id) => {
    const params =
      typeChart === typePP.WPP ? 'IncludeWindSpeedAndPower=true' : 'IncludeInsolationAndOutput=true'

    const requestUrl = id
      ? `${API_URL}/${typeChart}/live?${params}&IncludeNameValue=true&PlantIds=${id}`
      : `${API_URL}/${typeChart}/live?${params}&IncludeNameValue=true`

    return axiosApiInstance.get(requestUrl)
  },

  getStations: async () => {
    const requestUrl = `${API_URL}/Plant/All`
    const response = await axiosApiInstance.get(requestUrl)

    const validate = v.validate(response.data, Schemas.PLANT_ALL, {
      base: requestUrl,
      nestedErrors: true,
    })

    if (!validate.valid && isLocalEnv) {
      showErrorMessageInConsole(validate.schema?.description, validate.toString())
    }
    return response
  },

  getDataCommonMetrics: (dateRange = {}) => {
    const requestUrl = `${API_URL}/Plant/CommonMetrics?from=${encodeURIComponent(
      dateRange.from,
    )}&to=${encodeURIComponent(dateRange.to)}`

    return axiosApiInstance.get(requestUrl)
  },

  // Unbalance API methods - controlled by feature flag
  ...(isApiEndpointEnabled(FlagPages.UNBALANCE_PAGE) && {
    getUnbalance: () => axiosApiInstance.get('/fake/all-charts.json'),

    getChartsUnbalance: (typeChart, id, dateRange = {}) => {
      const requestUrl = id
        ? `${API_URL}/${typeChart}/Unbalance?id=${id}&from=${encodeURIComponent(
            dateRange.from,
          )}&to=${encodeURIComponent(dateRange.to)}`
        : `${API_URL}/${typeChart}/Unbalance`

      return axiosApiInstance.get(requestUrl)
    },

    getMinMaxDateUnbalance: (typeChart, id) => {
      const requestUrl = `${API_URL}/${typeChart}/EnergyForecastPeriod?id=${id}`
      return axiosApiInstance.get(requestUrl)
    },

    getUnbalanceDeviations: (typeChart, id, dateRange) => {
      const requestUrl = id
        ? `${API_URL}/${typeChart}/UnbalanceDeviations?id=${id}&from=${encodeURIComponent(
            dateRange.from,
          )}&to=${encodeURIComponent(dateRange.to)}`
        : `${API_URL}/${typeChart}/UnbalanceDeviations?from=${encodeURIComponent(
            dateRange.from,
          )}&to=${encodeURIComponent(dateRange.to)}`

      return axiosApiInstance.get(requestUrl)
    },

    getGeneralUnbalance: () =>
      Promise.all([API.getChartsUnbalance(typePP.WPP, 0), API.getChartsUnbalance(typePP.SPP, 0)]),
  }),

  // Business Plan API methods
  uploadFile: file =>
    axiosApiInstance.post(`${API_URL}/BusinessPlan/update`, file, {
      contentType: 'multipart/form-data',
    }),

  downloadFile: (typeReport = null) => {
    const requestUrl = `${API_URL}/BusinessPlan/${
      typeReport === 'template' ? 'template/download' : 'download-short'
    }`

    return axiosApiInstance.get(requestUrl, {
      responseType: 'arraybuffer',
    })
  },

  getDataBusinessPlan: async ({isDetails}) => {
    const requestUrl = `${API_URL}/BusinessPlan${isDetails ? '/details' : ''}`

    const response = await axiosApiInstance.get(requestUrl)

    const validate = v.validate(
      response.data,
      isDetails ? Schemas.BUSINESS_PLAN_DETAILS : Schemas.BUSINESS_PLAN_TABLE,
      {
        base: requestUrl,
        nestedErrors: true,
      },
    )

    if (!validate.valid && isLocalEnv) {
      showErrorMessageInConsole(validate.schema?.description, validate.toString())
    }

    return response
  },

  // Forecast API methods
  getForecastList: typeStation =>
    axiosApiInstance.get(`${API_URL}/${typeStation}/ForecastProviders`),

  getForecastMetrics: ({typeStation, providerIds, plantId, dateRange, horizon}) => {
    const params = `?plantId=${plantId}&providerIds=${providerIds}&horizon=${horizon}&from=${encodeURIComponent(
      dateRange.from,
    )}&to=${encodeURIComponent(dateRange.to)}`

    return axiosApiInstance.get(`${API_URL}/${typeStation}/Forecast${params}`)
  },

  // Restrictions Journal API - controlled by feature flag
  ...(isApiEndpointEnabled(FlagPages.RESTRICTIONS_JOURNAL_PAGE) && {
    getRestrictionsJournalTable: async params => {
      const param = queryString.stringify({
        limit: params?.pagination?.limit,
        page: get(params.pagination, 'page', 0) + 1,
        plantIds: params?.stationsKey,
        requestStatus: params?.requestStatus,
        from: params?.dateRange?.from ? params?.dateRange?.from : undefined,
        to: params?.dateRange?.to ? params?.dateRange?.to : undefined,
      })

      const requestUrl = `${API_URL}/RestrictionLog?${param}`

      const response = await axiosApiInstance.get(requestUrl)

      const validate = v.validate(response.data, Schemas.RESTRICTION_LOG, {
        base: requestUrl,
        nestedErrors: true,
      })

      if (!validate.valid && isLocalEnv) {
        showErrorMessageInConsole(validate.description, validate.toString())
      }
      return response
    },

    getRestrictionsJournalItemById: id => axiosApiInstance.get(`${API_URL}/RestrictionLog/${id}`),

    createRestrictionsJournalItem: body => axiosApiInstance.post(`${API_URL}/RestrictionLog`, body),

    updateRestrictionsJournalItem: body =>
      axiosApiInstance.patch(`${API_URL}/RestrictionLog`, body),

    deleteRestrictionsJournalItem: id =>
      axiosApiInstance.delete(`${API_URL}/RestrictionLog/?id=${id}`),

    getLossesSum: params => {
      const param = queryString.stringify({
        PlantIds: params?.plantId,
        RequestStatus: 1,
        From: params?.dateRange?.from ? params?.dateRange?.from : undefined,
        To: params?.dateRange?.to ? params?.dateRange?.to : undefined,
      })

      const requestUrl = `${API_URL}/RestrictionLog/LossesSum?${param}`

      return axiosApiInstance.get(requestUrl)
    },
  }),

  // Map API - controlled by feature flag
  ...(isApiEndpointEnabled(FlagPages.MAP_PAGE) && {
    getStationsForMap: () => axiosApiInstance.get(`${API_URL}/Plant/AllForMap`),
  }),

  // Operational Journal API
  getOperationalJournalTable: params => {
    const param = queryString.stringify({
      limit: params?.pagination?.limit,
      offset: params?.pagination?.offset,
      plantId: params?.stationOJId,
      from: params?.dateRange?.from,
      to: params?.dateRange?.to,
      equipmentIds: params?.equipmentIds,
      recordTypeIds: params?.recordTypeIds,
      createdByEmails: params?.createdByEmails,
      sortBy: params?.sorting?.sortBy,
      ...(params?.sorting?.sortOrder && {
        sortOrder: params.sorting.sortOrder === SortOrderEnum.ASC ? 'Ascending' : 'Descending',
      }),
    })

    const requestUrl = `${API_URL}/OperationalLog/list?${param}`

    return axiosApiInstance.get(requestUrl)
  },

  getOJRecordTypes: () => axiosApiInstance.get(`${API_URL}/OperationalLog/RecordTypes`),

  getOJEquipments: id =>
    axiosApiInstance.get(`${API_URL}/OperationalLog/Dictionaries/Station/${id}/Equipments`),

  createOJEquipment: body =>
    axiosApiInstance.post(`${API_URL}/OperationalLog/CreateEquipment`, body),

  getOJPlants: () => axiosApiInstance.get(`${API_URL}/OperationalLog/Plants`),

  getOJFilterOptions: id =>
    axiosApiInstance.get(`${API_URL}/OperationalLog/FilterOptions/plant/${id}`),

  getOJItemById: id => axiosApiInstance.get(`${API_URL}/OperationalLog/${id}`),

  getOJItemHistory: id => axiosApiInstance.get(`${API_URL}/OperationalLog/${id}/History`),

  createOJItem: body => axiosApiInstance.post(`${API_URL}/OperationalLog/create`, body),

  updateOJItem: (id, body) => axiosApiInstance.patch(`${API_URL}/OperationalLog/${id}`, body),

  toggleIsDeviationOJItem: id =>
    axiosApiInstance.patch(`${API_URL}/OperationalLog/${id}/toggleIsDeviation`),

  sendRequestForEditRequestOJItem: id =>
    axiosApiInstance.post(`${API_URL}/OperationalLog/${id}/SendRequestForEditRecordItem`),

  acceptRequestForEditRecordItem: id =>
    axiosApiInstance.post(`${API_URL}/OperationalLog/${id}/AcceptRequestForEditRecordItem`),

  rejectRequestForEditRecordItem: id =>
    axiosApiInstance.post(`${API_URL}/OperationalLog/${id}/RejectRequestForEditRecordItem`),

  // Operational Journal API - Shift operations
  getShiftStatus: plantId =>
    axiosApiInstance.get(`${API_URL}/OperationalLog/ShiftStatus/${plantId}`),

  getShiftTransfer: plantId =>
    axiosApiInstance.get(`${API_URL}/OperationalLog/TransferShift/${plantId}`),

  sendShiftTransfer: ({plantId, ...body}) =>
    axiosApiInstance.post(`${API_URL}/OperationalLog/TransferShift/${plantId}`, body),

  getShiftAcceptance: () => axiosApiInstance.get(`${API_URL}/OperationalLog/AcceptingShift`),

  sendShiftAcceptance: () => axiosApiInstance.post(`${API_URL}/OperationalLog/AcceptingShift`),

  getShiftTransferHistoryTable: params => {
    const param = queryString.stringify({
      limit: params?.pagination?.limit,
      offset: params?.pagination?.offset,
      plantId: params?.stationId,
      logDateAccepting: params?.dateRange?.from,
      logDateTransfer: params?.dateRange?.to,
      executorEmails: params?.executorEmails,
      sortBy: params?.sorting?.sortBy,
      ...(params?.sorting?.sortOrder && {
        sortOrder: params.sorting.sortOrder === SortOrderEnum.ASC ? 'Ascending' : 'Descending',
      }),
    })
    return axiosApiInstance.get(`${API_URL}/OperationalLog/ShiftTransferHistory?${param}`)
  },

  getShiftTransferHistoryFilterOptions: () =>
    axiosApiInstance.get(`${API_URL}/OperationalLog/FilterOptions/ShiftTransferHistory`),

  getShiftTransferHistoryById: id =>
    axiosApiInstance.get(`${API_URL}/OperationalLog/ShiftTransferHistory/${id}`),

  // Administration API - controlled by feature flag
  ...(isApiEndpointEnabled(FlagPages.ADMINISTRATION_PAGE) && {
    getAdministrationTable: params => {
      const param = queryString.stringify({
        limit: params?.pagination?.limit,
        page: get(params?.pagination, 'page', 0) + 1,
        search: params?.search,
        plantIds: params?.plantIds,
        roleIds: params?.roleIds,
        sortOrder: params?.sorting?.sortOrder === SortOrderEnum.ASC ? 0 : 1,
        sortBy: params?.sorting?.sortBy,
      })

      return axiosApiInstance.get(`${API_URL}/administration/User?${param}`)
    },

    deleteAdministrationItem: email =>
      axiosApiInstance.delete(`${API_URL}/administration/User?email=${email}`),

    getAdministrationItemByEmail: email =>
      axiosApiInstance.get(`${API_URL}/administration/User/${email}`),

    createAdministrationItem: body => axiosApiInstance.put(`${API_URL}/administration/User`, body),

    updateAdministrationItem: body =>
      axiosApiInstance.patch(`${API_URL}/administration/User`, body),
  }),
}

export default API
